import moment from 'moment';

// Match kickoff still uses convertUTCTimeToLocal. Date-only fields use the helpers below.

export const checkIfDateIsPast = (date: Date): boolean => {
    const dateNow = new Date();
    dateNow.setHours(0, 0, 0, 0);

    if (date < dateNow) {
        return true;
    }

    return false;
};

export const convertUTCTimeToLocal = (date: Date): Date => {
    const utcOffset = moment(date).utcOffset();
    return moment(date).add(utcOffset, 'minutes').toDate();
};

export const checkIfDatesWithoutTimeAreSame = (firstDate: Date, secondDate?: Date): boolean => {
    if (!secondDate) return false;

    return moment(firstDate).isSame(secondDate, 'day');
};

type CalendarDay = { year: number; month: number; day: number };

type StoredClock = CalendarDay & {
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
};

// NSwag does `new Date("1995-05-10T00:00:00")`. That string has no offset, so the local
// clock parts are the stored clock. moment.utc of those parts prints the stored day
// without adding the browser offset again.
const storedClock = (value: Date | string | moment.Moment): StoredClock | undefined => {
    if (typeof value === 'string') {
        const parsed = moment.utc(value);
        if (!parsed.isValid()) return undefined;
        return {
            year: parsed.year(),
            month: parsed.month(),
            day: parsed.date(),
            hour: parsed.hour(),
            minute: parsed.minute(),
            second: parsed.second(),
            millisecond: parsed.millisecond(),
        };
    }

    if (moment.isMoment(value)) {
        if (!value.isValid()) return undefined;
        return {
            year: value.year(),
            month: value.month(),
            day: value.date(),
            hour: value.hour(),
            minute: value.minute(),
            second: value.second(),
            millisecond: value.millisecond(),
        };
    }

    if (Number.isNaN(value.getTime())) return undefined;
    return {
        year: value.getFullYear(),
        month: value.getMonth(),
        day: value.getDate(),
        hour: value.getHours(),
        minute: value.getMinutes(),
        second: value.getSeconds(),
        millisecond: value.getMilliseconds(),
    };
};

const addUtcDays = (year: number, month: number, day: number, days: number): CalendarDay => {
    const next = new Date(Date.UTC(year, month, day + days));
    return { year: next.getUTCFullYear(), month: next.getUTCMonth(), day: next.getUTCDate() };
};

// Last Sunday of `month` (1-12) at 01:00 UTC, as a unix millisecond. Sunday is day 0.
const lastSundayAt0100 = (year: number, month: number): number => {
    const last = new Date(Date.UTC(year, month, 0));
    return Date.UTC(year, month, -last.getUTCDay(), 1, 0, 0);
};

// Same offset as DateOnlyClock. Years before 1983, including 1941-1945, are UTC+1.
// From 1983, EU DST is last Sunday of March 01:00 UTC inclusive until the fall
// transition 01:00 UTC exclusive. The fall transition is the last Sunday of September
// through 1995, and the last Sunday of October from 1996.
const zagrebOffsetHours = (clock: StoredClock): number => {
    if (clock.year < 1983) return 1;
    const utcMs = Date.UTC(
        clock.year,
        clock.month,
        clock.day,
        clock.hour,
        clock.minute,
        clock.second,
        clock.millisecond
    );
    const start = lastSundayAt0100(clock.year, 3);
    const end = lastSundayAt0100(clock.year, clock.year <= 1995 ? 9 : 10);
    return utcMs >= start && utcMs < end ? 2 : 1;
};

const atEndOfCalendar = (clock: StoredClock): boolean =>
    clock.year === 9999 && clock.month === 11 && clock.day === 31;

// Same rule as DateOnlyClock.Normalize. Whole hours 0-12 stay on that calendar day.
// Whole hours 13-23 roll to the next day. Any other time takes the explicit Zagreb
// civil date of that clock read as UTC. Years through 1901 stay on the stored day.
// 9999-12-31 is left on that day when the next day does not exist.
export const dateOnlyCalendarDay = (
    value: Date | string | moment.Moment | null | undefined
): CalendarDay | undefined => {
    if (value == null || value === '') return undefined;
    const clock = storedClock(value);
    if (!clock) return undefined;
    if (clock.year <= 1901) {
        return { year: clock.year, month: clock.month, day: clock.day };
    }

    const wholeHour = clock.minute === 0 && clock.second === 0 && clock.millisecond === 0;
    if (wholeHour) {
        if (clock.hour >= 13 && atEndOfCalendar(clock)) {
            return { year: clock.year, month: clock.month, day: clock.day };
        }
        return clock.hour >= 13
            ? addUtcDays(clock.year, clock.month, clock.day, 1)
            : { year: clock.year, month: clock.month, day: clock.day };
    }

    const offset = zagrebOffsetHours(clock);
    if (atEndOfCalendar(clock) && clock.hour + offset >= 24) {
        return { year: clock.year, month: clock.month, day: clock.day };
    }
    const local = new Date(
        Date.UTC(
            clock.year,
            clock.month,
            clock.day,
            clock.hour + offset,
            clock.minute,
            clock.second,
            clock.millisecond
        )
    );
    return { year: local.getUTCFullYear(), month: local.getUTCMonth(), day: local.getUTCDate() };
};

// UTC midnight of the calendar day the user picked. DatePicker moments are local.
export function toDateOnly(value: moment.Moment | Date): Date;
export function toDateOnly(value: moment.Moment | Date | null | undefined): Date | undefined;
export function toDateOnly(value: moment.Moment | Date | null | undefined): Date | undefined {
    if (value == null) return undefined;
    const picked = moment.isMoment(value) ? value : moment(value);
    if (!picked.isValid()) return undefined;
    return new Date(Date.UTC(picked.year(), picked.month(), picked.date()));
}

// Rounded calendar day, via moment.utc, so a shifted 22:00 row shows the intended day
// before the data fix and a fixed 00:00 row shows the same day afterwards.
export const formatDateOnly = (
    value: Date | string | null | undefined,
    format = 'DD.MM.YYYY.'
): string => {
    const day = dateOnlyCalendarDay(value);
    if (!day) return '';
    return moment.utc([day.year, day.month, day.day]).format(format);
};

// Edit forms. Old 22:00/23:00 rows and fixed 00:00 rows both open on the intended day.
export const dateOnlyFormMoment = (
    value: Date | string | null | undefined
): moment.Moment | undefined => {
    const day = dateOnlyCalendarDay(value);
    if (!day) return undefined;
    return moment({ year: day.year, month: day.month, date: day.day });
};
